<template>
  <div class="booking-air-uld-data-grid-ae">
    <cargo-ag-grid
      :grid-options="gridOptions"
      :column-defs="finalColumnDefs"
      :show-side-bar="false"
      :row-data="finalRowData"
      :auto-fit-content="false"
      style="width:100%;height:200px;margin-bottom: 5px"
      @gridReady="params=>{gridApi=params.api}"
    />
    <div style="width:100%">
      <div style="text-align: right;margin-bottom: 5px">
        <el-button v-show="isEdit" no="Add" type="primary" @click="add">{{ $t('SeaExport.Add') }}</el-button>&nbsp;
        <el-button v-show="isEdit || (isEdit && rowData && rowData.length > 1)" no="Delete" type="danger" @click="remove">{{ $t('SeaExport.Delete') }}</el-button>
      </div>
    </div>
  </div>
</template>

<script>

import { UnitLoadDeviceUtil } from '@/class/cargo/model/utils/UnitLoadDeviceUtil'
import { getSpecificBasicData } from '@/utils/basicDataCache'
import { basicCacheKeys } from '@/utils/cacheConfig'
import { EntityDefaultValueUtil } from '@/class/cargo/model/utils/EntityDefaultValueUtil'
import ContainerNoHelper from '@/utils/containerNoHelper'
import { AIRPLANE_CONTAINER_TYPES } from '@/class/cargo/model/parameters/SystemConstants'
import CargoAgGridAdvanceSelectEditor from '@/components/Cargo/Common/CargoAgGridAdvanceSelectEditor'
import agGridColumnEdit from '@/components/Cargo/grid/agGridColumnEdit'
import * as SystemConstants from '@/class/cargo/model/parameters/SystemConstants'
import * as CommonState from '@/class/cargo/model/parameters/CommonState'
import { AirUld } from '@/class/model/booking/AirUld'
import uidUtil from '@/utils/uidUtil'
import CargoAgGridCommonInputEditor from '@/components/Cargo/Common/CargoAgGridCommonInputEditor'
import CustomizationAgGridHeader from '@/components/Cargo/Common/CustomizationAgGridHeader'

export default {
  name: 'BookingAirUldDataGrid',
  components: { },
  props: {
    job: { type: Object, required: true },
    value: { type: Array, default: () => [] },
    isEdit: { type: Boolean, required: true },
    hideContainerNo: { type: Boolean, default: true }
  },
  data() {
    return {
      airplaneContainers: [],
      airplaneContainerTypes: AIRPLANE_CONTAINER_TYPES,
      eqTypes: [],
      defaultAirUld: {},
      gridOptions: {
        rowSelection: 'single',
        singleClickEdit: true,
        suppressCellSelection: true,
        suppressContextMenu: true,
        rowHeight: 32,
        unSortIcon: true,
        onCellFocused: (event) => {
          this.onUpCellFocused(event)
        },
        onRowClicked: this.rowClicked,
        context: {
          componentParent: this
        },
        frameworkComponents: {
          agGridColumnEdit,
          CargoAgGridCommonInputEditor,
          CargoAgGridAdvanceSelectEditor: CargoAgGridAdvanceSelectEditor,
          CustomizationAgGridHeader
        }
      },
      rowData: [],
      gridApi: null,
      selectedAirUld: null // 当前选中的记录
    }
  },
  computed: {
    defaultContainerCode() {
      return this.defaultAirUld.containerCode ? this.defaultAirUld.containerCode : '20'
    },
    defaultEqType() {
      return this.defaultAirUld.eqType ? this.defaultAirUld.eqType : 'GP'
    },
    finalRowData() {
      return this.rowData.filter(p => !p.deleted)
    },
    fitSize() {
      if (this.hideContainerNo) {
        return 110
      }
      return 90
    },
    finalColumnDefs() {
      return [
        {
          headerName: this.$t('CargoMsg.Cargo_ApplicableContainerType'),
          field: 'containerType',
          width: this.fitSize,
          suppressMenu: true,
          lockVisible: true,
          editable: this.isEdit,
          cellEditor: 'CargoAgGridAdvanceSelectEditor',
          cellEditorParams: {
            selectProps: {
              staticOptions: this.airplaneContainerTypes,
              filterable: true,
              clearable: false,
              tagName: 'chineseName',
              tagValue: 'code',
              filterTagNames: ['code', 'chineseName']
            }
          },
          valueFormatter: params => {
            return CommonState.getNameByCode(SystemConstants.AIRPLANE_CONTAINER_TYPES, params.value)
          },
          onCellValueChanged: p => { this.onCellChange(p) }
        },
        {
          headerName: this.$t('CargoMsg.Cargo_ApplicableContainerCode'),
          field: 'containerCode',
          width: this.fitSize,
          suppressMenu: true,
          editable: this.isEdit,
          cellEditor: 'CargoAgGridAdvanceSelectEditor',
          cellEditorParams: (params) => {
            const containerTypes = this.airplaneContainers.filter(item => item.type === params.data.containerType)
            return {
              selectProps: {
                staticOptions: containerTypes || [],
                clearable: false,
                filterable: true,
                tagName: 'code',
                tagValue: 'code',
                filterTagNames: ['code']
              }
            }
          },
          valueFormatter: params => {
            return params.value
          },
          onCellValueChanged: p => { this.onCellChange(p) }
        },
        {
          headerName: this.$t('CargoMsg.Cargo_AmountNumber'),
          field: 'quantity',
          width: this.fitSize,
          suppressMenu: true,
          editable: this.isEdit,
          cellEditor: 'CargoAgGridCommonInputEditor',
          cellEditorParams: {
            isNumber: true,
            inputProps: {
              controls: false,
              precision: 0
            }
          },
          onCellValueChanged: p => { this.onCellChange(p) }
        },
        {
          headerName: this.$t('CargoMsg.cargo_pallet'),
          headerComponent: 'CustomizationAgGridHeader',
          headerComponentParams: {
            title: this.$t('CargoMsg.Cargo_Board_Tips')
          },
          field: 'board',
          suppressMenu: true,
          width: 100,
          cellRendererFramework: 'agGridColumnEdit',
          custom: {
            isEdit: false,
            tag: 'el-checkbox',
            valueChanged: p => { this.onCellChange(p) }
          }
        }
      ]
    }
  },
  watch: {
    value(newVal) {
      this.rowData = newVal
    },
    isEdit(newVal) {
      for (const item of this.finalColumnDefs) {
        if (item.custom instanceof Object) {
          item.custom.isEdit = this.isEdit
        }
      }
    }
  },
  async created() {
    this.airplaneContainers = await getSpecificBasicData(basicCacheKeys.basic_data_AirplaneContainer)
    await this.getDefaultAirUld()
    this.rowData = this.value
    for (const item of this.finalColumnDefs) {
      if (item.custom instanceof Object) {
        item.custom.isEdit = this.isEdit
      }
    }
  },
  methods: {
    async getDefaultAirUld() {
      const entityDefValUtil = EntityDefaultValueUtil.getInstance()
      if (!entityDefValUtil.defaultValues) {
        await entityDefValUtil.initEntityDefaultValue()
      }
      this.defaultAirUld = entityDefValUtil.airUld
    },
    add() {
      let containerCode = ''
      let containerType = ''
      if (!this.airplaneContainers || this.airplaneContainers.length === 0) {
        this.$message.warning(this.$t('CargoMsg.Cargo_Container_Type_Not_Configured'))
        return
      }
      if (this.finalRowData.length === 0) {
        // 获取基础数据-集装器的第一条数据为默认值
        if (this.airplaneContainers && this.airplaneContainers.length > 0) {
          containerType = this.airplaneContainers[0].type
          containerCode = this.airplaneContainers[0].code
        }
      } else {
        // 遍历finalRowData，如果存在相同的集装器类型，则取第一个不同的集装器代码
        if (this.airplaneContainers && this.airplaneContainers.length > 0) {
          // 遍历airplaneContainers中的code, 和type,如果finalRowData中存在相同的containerType和containerCode,则取第一个不同的集装器代码
          for (const container of this.airplaneContainers) {
            const data = this.finalRowData.filter(p => p.containerType === container.type && p.containerCode === container.code)
            if (!data || data.length === 0) {
              containerType = container.type
              containerCode = container.code
              break
            }
          }
        }
      }
      if (!containerCode) {
        this.$message.warning(this.$t('SeaExport.RepeatUldType'))
        if (this.airplaneContainers && this.airplaneContainers.length > 0) {
          containerType = this.airplaneContainers[0].type
          containerCode = this.airplaneContainers[0].code
        }
      }
      const row = new AirUld().fillDefaultValue().getData()
      Object.assign(row, this.defaultAirUld)
      row.containerCode = containerCode
      row.containerType = containerType
      row.quantity = 1
      row.jobUuid = this.job.uuid
      if (this.job.masterFlag) {
        row.joinContainerId = uidUtil.createUID()
      }
      this.rowData.push(row)
      this.$nextTick(() => {
        if (this.gridApi) {
          this.gridApi.deselectAll()
          const nodes = this.gridApi.getRenderedNodes()
          const lastNode = nodes[nodes.length - 1]
          lastNode.setSelected(true)
          this.gridApi.setFocusedCell(nodes.length - 1, 'containerCode')
          this.selectedAirUld = lastNode.data
        }
      })

      this.job.airUld = this.rowData // 指向同一个 airUld
      // 装箱信息
      if (this.job.masterFlag) {
        UnitLoadDeviceUtil.autoAdjustMasterContainer(this.job)
      } else {
        UnitLoadDeviceUtil.autoJustUnitLoadDeviceUsages(this.job)
      }
    },
    remove() {
      if (!this.selectedAirUld) {
        return
      }
      if (this.selectedAirUld.uuid) {
        // 检查: 已输入箱型箱种信息不能删
        if (this.job.masterFlag && !UnitLoadDeviceUtil.checkCanDelAirUld(this.selectedAirUld, this.job)) {
          this.$message.info(this.$t('Message.RelevantInstallationInfoHasBeenEnteredCannotBeDeleted'))
          return
        }
        // 检查该箱型箱种能删的数量
        if (!this.job.masterFlag && !UnitLoadDeviceUtil.checkInputContainerNo(this.selectedAirUld, this.job)) {
          this.$message.info(this.$t('Message.RelevantInstallationInfoHasBeenEnteredCannotBeDeleted'))
          return
        }
        this.selectedAirUld.deleted = true
        const index = this.rowData.findIndex(p => p.uuid === this.selectedAirUld.uuid)
        if (index > -1) {
          this.rowData.splice(index, 1)
        }
        if (!this.job.delAirUldUuids) {
          this.job.delAirUldUuids = []
        }
        this.job.delAirUldUuids.push(this.selectedAirUld.uuid)
      } else {
        const index = this.rowData.findIndex(p => p === this.selectedAirUld)
        if (index > -1) {
          this.rowData.splice(index, 1)
        }
      }
      this.job.airUld = this.rowData // 指向同一个 airUld
      // 更新装箱信息中的箱型信息
      if (this.job.masterFlag) {
        UnitLoadDeviceUtil.autoAdjustMasterContainer(this.job)
      } else {
        UnitLoadDeviceUtil.autoJustUnitLoadDeviceUsages(this.job)
      }
      this.selectedAirUld = null
    },
    async onCellChange(params) {
      console.info('cellChange', params)
      this.$emit('cellChange', params)
      const field = params.hasOwnProperty('field') ? params.field : params.colDef.field
      // 如果类型发生了改变，则删除原有的集装器代码
      if (field === 'containerType' && params.newValue !== params.oldValue) {
        const airplaneContainers = this.airplaneContainers.filter(item => item.type === params.newValue)
        params.data.containerCode = airplaneContainers && airplaneContainers.length > 0 ? airplaneContainers[0].code : ''
      }
      // 箱型箱种输入验证
      if (field === 'containerCode' || field === 'containerType') {
        if (UnitLoadDeviceUtil.checkSeaEq(this.job, params.data)) {
          this.$message.warning(this.$t('SeaExport.RepeatUldType'))
          return
        }
        if (field === 'containerType' && params.newValue !== params.oldValue) {
          const containerTypes = this.airplaneContainers.filter(item => item.type === params.data.containerType)
          params.data.containerCode = containerTypes && containerTypes.length > 0 ? containerTypes[0].code : ''
        }
      }

      // 装箱信息 （board 编辑不影响container,usages 增删改）
      if (field !== 'board') {
        if (this.job.masterFlag) {
          UnitLoadDeviceUtil.autoAdjustMasterContainer(this.job)
        } else {
          UnitLoadDeviceUtil.autoJustUnitLoadDeviceUsages(this.job)
        }
      }
      // soc状态改变
      if (field === 'board') {
        if (this.job.unitLoadDevices) {
          for (const c of this.job.unitLoadDevices) {
            if (c.containerType === params.data.containerType && c.containerCode === params.data.containerCode && c.unitLoadDeviceUsage) {
              c.board = params.data.board
              for (const usage of c.unitLoadDeviceUsage) {
                usage.board = params.data.board
              }
            }
            c.isDirty = true
          }
        }
        if (this.job.unitLoadDeviceUsages) {
          for (const c of this.job.unitLoadDeviceUsages) {
            if (c.containerType === params.data.containerType && c.containerCode === params.data.containerCode) {
              c.board = params.data.board
            }
          }
        }
      }
      // 刷新数据
      this.gridOptions.api.refreshCells({ force: true })
    },
    rowClicked(event) {
      if (event) {
        this.selectedAirUld = event.data
      }
    },
    onUpCellFocused(event) {
      if (this.gridOptions.api && event.column) {
        this.gridOptions.api.forEachNode(node => {
          if (node.rowIndex === event.rowIndex) {
            node.setSelected(true)
            this.selectedAirUld = node.data
          }
        })
      }
    },
    containerNbrStyleFunction(containerNo) {
      if (containerNo) {
        if (!ContainerNoHelper.checkContainerNo(containerNo.containerNo)) {
          return 'data-red'
        }
      }
      return ''
    },
    clear() {
      const job = this.job
      /* 清空airUld */
      job.delAirUldUuids = job.delAirUldUuids || []
      for (const item of this.rowData) {
        if (item.uuid) {
          job.delAirUldUuids.push(item.uuid)
        }
      }
      this.rowData.splice(0, this.rowData.length)
      /* 清空unitLoadDevices */
      if (job.unitLoadDevices) {
        for (const item of job.unitLoadDevices) {
          if (item.uuid) {
            job.unitLoadDevices.push(item.uuid)
          }
        }
        job.unitLoadDevices.splice(0, job.unitLoadDevices.length)
      }
      // 清空unitLoadDeviceUsages
      job.unitLoadDeviceUsages.splice(0, job.unitLoadDeviceUsages.length)
    }
  }
}
</script>

<style lang="scss">
.booking-sea-eq-data-grid {
  .ag-header-cell-text {
    color: #4283ff;
    font-weight: bold;
  }
}
booking-air-uld-data-grid-ae {
  .ag-header-cell-text {
    font-weight: bold;
  }
  .data-red {
     input.el-input__inner {
       color: #FF0000;
     }
   }
}
</style>
